import { useState, useEffect, useCallback } from 'react';

const INPUT = {
  display: 'block', width: '100%', boxSizing: 'border-box',
  padding: '8px 10px', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6, fontSize: 13, color: '#f1f5f9',
  background: 'rgba(0,0,0,0.25)', outline: 'none',
};
const LABEL = { fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 4, display: 'block' };

const BLANK = { label: '', host: '', port: 993, user: '', pass: '', tls: true, mailbox: 'INBOX' };

// basePath lets other tools reuse this panel with their own account store
// (e.g. the Receipt Scanner uses basePath="/api/receipts/settings").
export default function ImapSettings({ onSaved, basePath = '/api/settings' }) {
  const [accounts, setAccounts]       = useState([]);
  const [editing, setEditing]         = useState(null); // { isNew?, id?, ...form }
  const [testState, setTestState]     = useState({});   // { [id]: 'testing'|'success'|errorMsg }
  const [folderMap, setFolderMap]     = useState({});   // { [id]: string[] }
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [saving, setSaving]           = useState(false);

  const reload = useCallback(async () => {
    try {
      const d = await fetch(basePath).then(r => r.json());
      setAccounts(d.imapAccounts || []);
      if (!(d.imapAccounts?.length)) {
        setEditing({ isNew: true, ...BLANK });
      }
    } catch {}
  }, [basePath]);

  useEffect(() => { reload(); }, [reload]);

  const loadFolders = useCallback(async (id) => {
    if (!id) return;
    setLoadingFolders(true);
    try {
      const d = await fetch(`${basePath}/accounts/${id}/folders`).then(r => r.json());
      if (d.ok) setFolderMap(m => ({ ...m, [id]: d.folders }));
    } catch {}
    finally { setLoadingFolders(false); }
  }, [basePath]);

  async function testAccount(id) {
    setTestState(t => ({ ...t, [id]: 'testing' }));
    try {
      const d = await fetch(`${basePath}/accounts/${id}/test`, { method: 'POST' }).then(r => r.json());
      setTestState(t => ({ ...t, [id]: d.ok ? 'success' : d.error }));
      if (d.ok) loadFolders(id);
    } catch (e) {
      setTestState(t => ({ ...t, [id]: e.message }));
    }
  }

  async function saveForm(thenTest = false) {
    if (!editing?.host || !editing?.user) return;
    setSaving(true);
    try {
      const d = await fetch(`${basePath}/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account: editing }),
      }).then(r => r.json());
      if (!d.ok) throw new Error(d.error || 'Save failed');
      const savedId = d.id;
      await reload();
      if (thenTest) {
        setEditing(e => e ? { ...e, id: savedId, isNew: false } : null);
        await testAccount(savedId);
      } else {
        setEditing(null);
        if (onSaved) onSaved();
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeAccount(id) {
    if (!window.confirm('Remove this inbox?')) return;
    await fetch(`${basePath}/accounts/${id}`, { method: 'DELETE' });
    if (editing?.id === id) setEditing(null);
    await reload();
  }

  function startEdit(account) {
    setEditing({
      isNew: false,
      id:      account.id,
      label:   account.label   || '',
      host:    account.host    || '',
      port:    account.port    || 993,
      user:    account.user    || '',
      pass:    account.hasPass ? '••••••••' : '',
      tls:     account.tls !== undefined ? account.tls : true,
      mailbox: account.mailbox || 'INBOX',
    });
  }

  const editId       = editing?.id;
  const editFolders  = editId ? (folderMap[editId] || []) : [];

  return (
    <div style={{ maxWidth: 560 }}>

      {/* Account list */}
      {accounts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {accounts.map(a => {
            const ts = testState[a.id];
            return (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: editing?.id === a.id ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${editing?.id === a.id ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 8, padding: '10px 14px',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.label || a.user}
                  </div>
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
                    {a.user} · {a.host}:{a.port} · {a.mailbox}
                  </div>
                </div>
                {ts && (
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 10, flexShrink: 0,
                    background: ts === 'success' ? 'rgba(52,211,153,0.15)' : ts === 'testing' ? 'rgba(255,255,255,0.06)' : 'rgba(248,113,113,0.15)',
                    color:      ts === 'success' ? '#34d399'               : ts === 'testing' ? '#64748b'                : '#f87171',
                  }}>
                    {ts === 'success' ? '✓ Connected' : ts === 'testing' ? 'Testing…' : '✗ Failed'}
                  </span>
                )}
                <Btn onClick={() => testAccount(a.id)} disabled={ts === 'testing'}>Test</Btn>
                <Btn onClick={() => { startEdit(a); loadFolders(a.id); }}>Edit</Btn>
                <Btn onClick={() => removeAccount(a.id)} danger>×</Btn>
              </div>
            );
          })}
        </div>
      )}

      {/* Add inbox button */}
      {!editing && (
        <button
          onClick={() => setEditing({ isNew: true, ...BLANK })}
          style={{
            width: '100%', textAlign: 'center', padding: '10px 0',
            background: 'rgba(255,255,255,0.03)', border: '2px dashed rgba(255,255,255,0.1)', borderRadius: 8,
            fontSize: 13, color: '#475569', cursor: 'pointer',
          }}
        >
          + Add inbox
        </button>
      )}

      {/* Add / Edit form */}
      {editing && (
        <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '16px 20px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', marginBottom: 14 }}>
            {editing.isNew ? 'Add inbox' : `Editing — ${editing.user || 'inbox'}`}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={LABEL}>Label</label>
            <input style={INPUT} value={editing.label} placeholder="e.g. Elk Kicks"
              onChange={e => setEditing(f => ({ ...f, label: e.target.value }))} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={LABEL}>IMAP Host</label>
              <input style={INPUT} value={editing.host} placeholder="imap.gmail.com"
                onChange={e => setEditing(f => ({ ...f, host: e.target.value }))} />
            </div>
            <div>
              <label style={LABEL}>Port</label>
              <input style={INPUT} type="number" value={editing.port}
                onChange={e => setEditing(f => ({ ...f, port: parseInt(e.target.value) }))} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={LABEL}>Email address</label>
            <input style={INPUT} type="email" value={editing.user} placeholder="you@company.com"
              onChange={e => setEditing(f => ({ ...f, user: e.target.value }))} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={LABEL}>Password / App password</label>
            <input style={INPUT} type="password" value={editing.pass} placeholder="••••••••"
              onChange={e => setEditing(f => ({ ...f, pass: e.target.value }))} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={LABEL}>
                Mailbox
                {editFolders.length === 0 && editId && (
                  <button
                    onClick={() => loadFolders(editId)}
                    disabled={loadingFolders}
                    style={{ marginLeft: 8, fontSize: 11, color: '#818cf8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                  >
                    {loadingFolders ? 'loading…' : 'load folders'}
                  </button>
                )}
              </label>
              {editFolders.length > 0 ? (
                <select style={{ ...INPUT, cursor: 'pointer' }} value={editing.mailbox}
                  onChange={e => setEditing(f => ({ ...f, mailbox: e.target.value }))}>
                  {editFolders.map(folder => <option key={folder} value={folder}>{folder}</option>)}
                </select>
              ) : (
                <input style={INPUT} value={editing.mailbox} placeholder="INBOX"
                  onChange={e => setEditing(f => ({ ...f, mailbox: e.target.value }))} />
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#94a3b8' }}>
                <input type="checkbox" checked={editing.tls}
                  onChange={e => setEditing(f => ({ ...f, tls: e.target.checked }))} />
                Use TLS (port 993)
              </label>
            </div>
          </div>

          {/* Test result for form */}
          {editId && testState[editId] && (
            <div style={{
              padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13,
              background: testState[editId] === 'success' ? 'rgba(52,211,153,0.12)'  : testState[editId] === 'testing' ? 'rgba(255,255,255,0.05)' : 'rgba(248,113,113,0.12)',
              color:      testState[editId] === 'success' ? '#34d399'                : testState[editId] === 'testing' ? '#64748b'                : '#f87171',
            }}>
              {testState[editId] === 'success' ? '✓ Connection successful'
                : testState[editId] === 'testing' ? 'Testing…'
                : `✗ ${testState[editId]}`}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setEditing(null)}
              style={{ fontSize: 13, color: '#64748b', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 7, padding: '8px 16px', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={() => saveForm(true)}
              disabled={saving || !editing.host || !editing.user}
              style={{
                fontSize: 13, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 7,
                padding: '8px 16px',
                cursor: saving || !editing.host || !editing.user ? 'not-allowed' : 'pointer',
                color:  saving || !editing.host || !editing.user ? '#334155' : '#94a3b8',
              }}
            >
              {saving ? 'Saving…' : 'Test connection'}
            </button>
            <button
              onClick={() => saveForm(false)}
              disabled={saving || !editing.host || !editing.user}
              style={{
                fontSize: 13, fontWeight: 600, borderRadius: 7, border: 'none',
                padding: '8px 20px',
                background: saving || !editing.host || !editing.user ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color:      saving || !editing.host || !editing.user ? '#334155' : '#fff',
                cursor:     saving || !editing.host || !editing.user ? 'not-allowed' : 'pointer',
                boxShadow:  saving || !editing.host || !editing.user ? 'none' : '0 2px 8px rgba(99,102,241,0.35)',
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Btn({ children, onClick, disabled, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: 12, padding: '4px 10px', borderRadius: 5, cursor: disabled ? 'not-allowed' : 'pointer',
        background: danger ? 'rgba(248,113,113,0.1)' : 'rgba(255,255,255,0.05)',
        color:      danger ? '#f87171' : disabled ? '#334155' : '#64748b',
        border:     `1px solid ${danger ? 'rgba(248,113,113,0.25)' : 'rgba(255,255,255,0.08)'}`,
      }}
    >
      {children}
    </button>
  );
}
